/**
 * Raketech Final Report
 * Full-period underpaid report: recon data (Mar 2023 – Nov 2024) + AC stats (Dec 2024+)
 * Shows all underpaid accounts with income source attribution and post-recon AC earnings.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  FileText,
  Loader2,
  AlertTriangle,
  Scale,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  ArrowDown,
  ArrowUp,
  Shield,
  Key,
  Calendar,
  Share2,
  Copy,
  Link,
  Lock,
  Trash2,
  X,
  Check,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface InvoiceMonth {
  month: string;
  ftds: number;
  revenue: number;
}

interface ComparisonIssue {
  type: string;
  severity: string;
  accountId: string;
  username: string;
  brand: string;
  brands?: string[];
  partner: string;
  offer: string;
  offers?: string[];
  geo: string;
  currency: string;
  details: string;

  invoiceFTDs?: number;
  invoiceRevenue?: number;
  postReconInvoiceRevenue?: number;
  reconciliationEarnings?: number;
  reconciliationEarningsOriginal?: number;
  reconciliationRows?: number;

  adwstatsCommission?: number;
  adwstatsCommissionOriginal?: number;
  adwstatsCurrency?: string;
  adwstatsAccountId?: string;
  adwstatsAccountName?: string;
  adwstatsMatchMethod?: string;
  adwstatsDataSynced?: boolean;

  partnerCommission?: number;
  partnerCommissionOriginal?: number;
  partnerCommissionOurShare?: number;

  manualCheckAmount?: number;
  manualCheckComment?: string;
  manualCheckSource?: string;

  revenueDifference?: number;
  percentageDifference?: number;
  exchangeRate?: number;

  missingInInvoice?: boolean;
  missingInReconciliation?: boolean;

  invoiceMonthly?: InvoiceMonth[];
  invoiceEntries?: { label: string; ftds: number; revenue: number; monthly: InvoiceMonth[] }[];

  reconMonthly?: { month: string; earnings: number; ftds: number }[];
  syncedMonthly?: { month: string; commission: number; ftds: number }[];
}

interface CompareData {
  period: string;
  issues: ComparisonIssue[];
  totals: {
    actualInvoiced: number;
    actualInvoiceCount: number;
    invoiced: number;
    reconEarnings: number;
    adwstatsSynced: number;
  };
}

interface PostReconRow {
  reconAccount: {
    accountId: string;
    username: string;
    partner: string;
    brand: string;
    offer: string;
    geo: string;
  };
  acMatch: { acId: number; name: string } | null;
  matchMethod: string;
  localAccountId?: string;
  postReconSynced: number;
  acReportCommission: number;
  acMonthly?: { month: string; commission: number }[];
  revenueDiff: number;
}

interface PostReconData {
  period: { recon: string; postRecon: string };
  summary: { totalReconAccounts: number; matched: number; missing: number };
  rows: PostReconRow[];
  extraAcAccounts: { acId: number; name: string; advertiser: string; acReportCommission: number }[];
}

// ---------------------------------------------------------------------------
// Report row with computed fields
// ---------------------------------------------------------------------------
interface ReportRow {
  issue: ComparisonIssue;
  reconInvoiced: number;
  postReconInvoiced: number;
  totalInvoiced: number;
  incomeAmount: number;
  incomeSource: string;
  incomeOriginal: number;
  incomeOriginalCurrency?: string;
  incomeOriginalAmount?: number;
  acPostRecon: number;
  totalEarned: number;
  underpaid: number;
  underpaidPct: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const getAuthToken = (): string | null => {
  try {
    const auth = localStorage.getItem('adwstats-auth');
    if (auth) { return JSON.parse(auth).state?.token || null; }
  } catch { /* ignore */ }
  return null;
};

const getACToken = (): string | null => {
  try {
    const stored = localStorage.getItem('affiliation-cloud-token');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.expiresAt && Date.now() < parsed.expiresAt) return parsed.token;
    }
  } catch { /* ignore */ }
  return null;
};

const saveACToken = (token: string) => {
  localStorage.setItem('affiliation-cloud-token', JSON.stringify({
    token, expiresAt: Date.now() + 23 * 60 * 60 * 1000,
  }));
};

const fmtCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(v);

const fmtOriginal = (v: number, currency: string) => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency} ${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  }
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const fmtMonth = (m: string) => {
  if (!m || m === 'unknown') return m;
  if (/^\d{4}-\d{2}$/.test(m)) {
    const [y, mo] = m.split('-');
    return `${MONTH_SHORT[parseInt(mo, 10) - 1] || mo} ${y}`;
  }
  return m;
};

const monthToSortKey = (m: string): string => {
  if (/^\d{4}-\d{2}$/.test(m)) return m;
  const slashMatch = m.match(/^\d{2}\/(\d{2})\/(\d{4})$/);
  if (slashMatch) return `${slashMatch[2]}-${slashMatch[1]}`;
  const parts = m.trim().split(/\s+/);
  if (parts.length === 2) {
    const idx = MONTH_NAMES.indexOf(parts[0]);
    if (idx >= 0) return `${parts[1]}-${String(idx + 1).padStart(2, '0')}`;
  }
  return m;
};

const sortMonthly = (months: InvoiceMonth[]): InvoiceMonth[] =>
  [...months].sort((a, b) => monthToSortKey(a.month).localeCompare(monthToSortKey(b.month)));

// Whether Adwin can independently verify an account at source.
type AccessTier = 'synced' | 'manual' | 'tracked' | 'network' | 'none';
interface AccessInfo { tier: AccessTier; has: boolean; label: string; detail: string; }
const getAccess = (iss: ComparisonIssue): AccessInfo => {
  if (iss.adwstatsDataSynced === true)
    return { tier: 'synced', has: true, label: 'Synced', detail: `Direct operator-backend sync${iss.adwstatsAccountName ? ` — ${iss.adwstatsAccountName}` : ''}` };
  if ((iss.manualCheckAmount || 0) > 0)
    return { tier: 'manual', has: true, label: 'Manual', detail: iss.manualCheckSource || 'Manually checked operator backend' };
  if (iss.adwstatsAccountId)
    return { tier: 'tracked', has: true, label: 'Tracked', detail: `Account connected in adwstats${iss.adwstatsAccountName ? ` — ${iss.adwstatsAccountName}` : ''} (no sync yet)` };
  return { tier: 'none', has: false, label: 'No access', detail: 'Recon-only — figure from Raketech reports, not independently verifiable' };
};
const accessColor = (tier: AccessTier) => {
  switch (tier) {
    case 'synced': return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    case 'manual': return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20';
    case 'tracked': return 'text-sky-400 bg-sky-500/10 border-sky-500/20';
    case 'network': return 'text-violet-400 bg-violet-500/10 border-violet-500/20';
    default: return 'text-red-400 bg-red-500/10 border-red-500/20';
  }
};

// Normalize a brand/account name for matching against our network logins.
const normName = (s: string | undefined | null): string =>
  (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

interface NetworkLogin { name: string; platform: string | null; }

// Coverage gap: invoiced months with NO earned data (no recon, no synced, no AC).
// These cannot be assessed as over- or under-paid — the comparison is blind there.
interface Coverage { uncoveredAmount: number; uncoveredMonths: Set<string>; }
const computeCoverage = (iss: ComparisonIssue, lastMonth: string, acMonths: Set<string>): Coverage => {
  const recCovered = new Set((iss.reconMonthly || []).filter(m => (m.earnings || 0) > 0).map(m => monthToSortKey(m.month)));
  const synCovered = new Set((iss.syncedMonthly || []).filter(m => (m.commission || 0) > 0).map(m => monthToSortKey(m.month)));
  const invMap = new Map<string, number>();
  if (iss.invoiceEntries && iss.invoiceEntries.length > 0) {
    for (const e of iss.invoiceEntries) for (const m of (e.monthly || [])) {
      const k = monthToSortKey(m.month); invMap.set(k, (invMap.get(k) || 0) + (m.revenue || 0));
    }
  } else {
    for (const m of (iss.invoiceMonthly || [])) {
      const k = monthToSortKey(m.month); invMap.set(k, (invMap.get(k) || 0) + (m.revenue || 0));
    }
  }
  let uncoveredAmount = 0; const uncoveredMonths = new Set<string>();
  for (const [k, rev] of invMap) {
    if (k > lastMonth || rev <= 0) continue;
    if (!(recCovered.has(k) || synCovered.has(k) || acMonths.has(k))) {
      uncoveredAmount += rev; uncoveredMonths.add(k);
    }
  }
  return { uncoveredAmount, uncoveredMonths };
};

type SortField = 'partner' | 'accountId' | 'reconInvoiced' | 'postReconInvoiced' | 'totalInvoiced' | 'income' | 'acPostRecon' | 'totalEarned' | 'underpaid' | 'pct';
type SortDir = 'asc' | 'desc';

type ReportMode = 'underpaid' | 'overpaid';

function computeReportRow(iss: ComparisonIssue, acPostRecon: number, mode: ReportMode = 'underpaid'): ReportRow | null {
  const reconInvoiced = iss.invoiceRevenue || 0;
  const postReconInvoiced = iss.postReconInvoiceRevenue || 0;
  const totalInvoiced = reconInvoiced + postReconInvoiced;

  const recon = iss.reconciliationEarnings || 0;
  const syncedRaw = iss.adwstatsCommission || 0;
  const synced95 = syncedRaw * 0.95;
  const manualRaw = iss.manualCheckAmount || 0;
  const manual95 = manualRaw * 0.95;

  let incomeAmount = recon;
  let incomeSource = 'Reconciliation';
  let incomeOriginal = recon;
  let incomeOriginalCurrency: string | undefined;
  let incomeOriginalAmount: number | undefined;

  if (recon > 0 && iss.reconciliationEarningsOriginal && iss.currency && iss.currency !== 'EUR') {
    incomeOriginalCurrency = iss.currency;
    incomeOriginalAmount = iss.reconciliationEarningsOriginal;
  }

  if (synced95 > incomeAmount) {
    incomeAmount = synced95;
    incomeSource = 'Synced From Operator (-5%)';
    incomeOriginal = syncedRaw;
    incomeOriginalCurrency = iss.adwstatsCurrency && iss.adwstatsCurrency !== 'EUR' ? iss.adwstatsCurrency : undefined;
    incomeOriginalAmount = incomeOriginalCurrency && iss.adwstatsCommissionOriginal ? iss.adwstatsCommissionOriginal : undefined;
  }
  if (manual95 > incomeAmount) {
    incomeAmount = manual95;
    incomeSource = 'Manually Checked Backend (-5%)';
    incomeOriginal = manualRaw;
    if (iss.partnerCommissionOriginal && iss.currency && iss.currency !== 'EUR') {
      incomeOriginalCurrency = iss.currency;
      incomeOriginalAmount = iss.partnerCommissionOriginal;
    } else {
      incomeOriginalCurrency = undefined;
      incomeOriginalAmount = undefined;
    }
  }

  const totalEarned = incomeAmount + acPostRecon;

  if (totalEarned <= 0 && totalInvoiced <= 0) return null;

  const underpaid = totalInvoiced - totalEarned;
  const underpaidPct = totalEarned > 0 ? (underpaid / totalEarned) * 100 : 0;

  if (mode === 'overpaid') {
    // Only show if overpaid (invoiced MORE than earned) — i.e. potential offsets Raketech may raise
    if (underpaid <= 1) return null;
  } else {
    // Only show if underpaid (invoiced less than earned)
    if (underpaid >= -1) return null;
  }

  return { issue: iss, reconInvoiced, postReconInvoiced, totalInvoiced, incomeAmount, incomeSource, incomeOriginal, incomeOriginalCurrency, incomeOriginalAmount, acPostRecon, totalEarned, underpaid, underpaidPct };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const RaketechFinalReport: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [compareData, setCompareData] = useState<CompareData | null>(null);
  const [postReconData, setPostReconData] = useState<PostReconData | null>(null);
  const [networkLogins, setNetworkLogins] = useState<NetworkLogin[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('underpaid');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [mode, setMode] = useState<ReportMode>('underpaid');
  const isOver = mode === 'overpaid';

  const switchMode = (m: ReportMode) => {
    setMode(m);
    setSortField('underpaid');
    // Underpaid: most negative first (asc). Overpaid: largest positive first (desc).
    setSortDir(m === 'overpaid' ? 'desc' : 'asc');
  };

  const [lastMonth, setLastMonth] = useState(getCurrentMonth);

  const [acToken, setAcToken] = useState<string | null>(getACToken);
  const [showTokenInput, setShowTokenInput] = useState(false);
  const [tokenInput, setTokenInput] = useState('');

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareTitle, setShareTitle] = useState('');
  const [shareDesc, setShareDesc] = useState('');
  const [sharePassword, setSharePassword] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [savedUrl, setSavedUrl] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [snapshots, setSnapshots] = useState<{ id: string; slug: string; title: string; lastMonth: string; createdAt: string; viewCount: number; hasPassword: boolean; url: string }[]>([]);
  const [showSnapshots, setShowSnapshots] = useState(false);

  const isACConnected = !!acToken;

  const fetchSnapshots = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const resp = await fetch('/api/raketech/report-snapshots', { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json() as any;
      if (json.success) setSnapshots(json.snapshots || []);
    } catch { /* ignore */ }
  }, []);

  const handleSaveSnapshot = async () => {
    const token = getAuthToken();
    if (!token || !reportRows.length) return;
    setIsSaving(true);
    try {
      const data = {
        rows: reportRows,
        totals,
        hasAcData: postReconData != null,
        period: {
          recon: '2023-03 to 2024-11',
          postRecon: postReconData?.period?.postRecon || null,
        },
        acMonthlyByAccountId: postReconData ? Object.fromEntries(
          postReconData.rows
            .filter(r => r.acMonthly && r.acMonthly.length > 0)
            .map(r => [r.reconAccount.accountId, r.acMonthly])
        ) : {},
      };
      const resp = await fetch('/api/raketech/report-snapshots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: shareTitle,
          description: shareDesc || undefined,
          password: sharePassword || undefined,
          lastMonth,
          data,
        }),
      });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || 'Failed');
      const fullUrl = `${window.location.origin}${json.snapshot.url}`;
      setSavedUrl(fullUrl);
      toast.success('Report snapshot saved');
      fetchSnapshots();
    } catch (err: any) {
      toast.error(err.message || 'Failed to save snapshot');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteSnapshot = async (id: string) => {
    const token = getAuthToken();
    if (!token) return;
    try {
      await fetch(`/api/raketech/report-snapshots?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Snapshot deleted');
      fetchSnapshots();
    } catch { toast.error('Failed to delete'); }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const fetchData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) { toast.error('Not authenticated'); return; }
    setIsLoading(true);
    setError(null);
    try {
      const resp = await fetch('/api/raketech/compare?invoicePeriod=all', { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json() as any;
      if (!json.success) throw new Error(json.error || 'Failed');
      if (json.data.empty) { setError(json.data.message); setCompareData(null); return; }
      setCompareData(json.data);

      const currentAcToken = acToken || getACToken();
      if (currentAcToken) {
        try {
          const postResp = await fetch(`/api/raketech/post-recon?lastMonth=${lastMonth}`, {
            headers: { Authorization: `Bearer ${token}`, 'X-Affiliation-Cloud-Token': currentAcToken },
          });
          const postJson = await postResp.json() as any;
          if (postJson.success) setPostReconData(postJson.data);
        } catch { /* ignore AC errors */ }
      }
    } catch (err: any) { setError(err.message); }
    finally { setIsLoading(false); }
  }, [acToken, lastMonth]);

  const fetchNetworks = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const resp = await fetch('/api/networks', { headers: { Authorization: `Bearer ${token}` } });
      const json = await resp.json() as any;
      if (!json.success || !Array.isArray(json.data)) return;
      const logins: NetworkLogin[] = [];
      for (const acc of json.data) {
        // Exclude pure Raketech/AC-imported mirrors (those are the disputed accounts, not our own logins),
        // unless we've actually synced data for them.
        const importSource = acc.metadata?.import_source;
        const hasData = !!acc.summary?.has_data;
        if (importSource === 'affiliation_cloud' && !hasData) continue;
        const platform = acc.platform_provider || acc.network?.name || null;
        for (const nm of [acc.account_name, acc.metadata?.advertiser_name, acc.network?.name]) {
          if (nm && String(nm).trim()) logins.push({ name: String(nm).trim(), platform });
        }
      }
      setNetworkLogins(logins);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchSnapshots(); }, [fetchSnapshots]);
  useEffect(() => { fetchNetworks(); }, [fetchNetworks]);

  // Index of our network logins, keyed by normalized name, for access lookup.
  const networkIndex = useMemo(() => {
    const exact = new Map<string, NetworkLogin>();
    const list: { key: string; info: NetworkLogin }[] = [];
    for (const l of networkLogins) {
      const key = normName(l.name);
      if (key.length < 4) continue;
      if (!exact.has(key)) exact.set(key, l);
      list.push({ key, info: l });
    }
    return { exact, list };
  }, [networkLogins]);

  const matchNetwork = useCallback((iss: ComparisonIssue): NetworkLogin | null => {
    const candidates = [iss.brand, ...(iss.brands || []), iss.adwstatsAccountName, iss.partner]
      .map(normName)
      .filter(c => c.length >= 4);
    for (const c of candidates) {
      const hit = networkIndex.exact.get(c);
      if (hit) return hit;
    }
    // startsWith match (handles "Big Boost NO" vs "Big Boost", "momang.se" vs "Momang")
    for (const c of candidates) {
      if (c.length < 5) continue;
      for (const { key, info } of networkIndex.list) {
        if (key.length < 5) continue;
        if (key.startsWith(c) || c.startsWith(key)) return info;
      }
    }
    return null;
  }, [networkIndex]);

  const resolveAccess = useCallback((iss: ComparisonIssue): AccessInfo => {
    const base = getAccess(iss);
    if (base.has) return base;
    const nm = matchNetwork(iss);
    if (nm) return { tier: 'network', has: true, label: 'Login', detail: `Login exists in /networks: ${nm.name}${nm.platform ? ` (${nm.platform})` : ''}` };
    return base;
  }, [matchNetwork]);

  const handleSaveToken = () => {
    if (!tokenInput.trim()) return;
    saveACToken(tokenInput.trim());
    setAcToken(tokenInput.trim());
    setTokenInput('');
    setShowTokenInput(false);
    toast.success('AC token saved');
    setTimeout(() => fetchData(), 100);
  };

  const handleDisconnect = () => {
    localStorage.removeItem('affiliation-cloud-token');
    setAcToken(null);
    setPostReconData(null);
    toast.info('Disconnected from AC');
  };

  // Build AC post-recon lookup
  const acPostReconByAccountId = useMemo(() => {
    const map = new Map<string, number>();
    if (postReconData) {
      for (const row of postReconData.rows) {
        if (row.acReportCommission > 0) {
          map.set(row.reconAccount.accountId, row.acReportCommission);
        }
      }
    }
    return map;
  }, [postReconData]);

  const acMonthlyByAccountId = useMemo(() => {
    const map = new Map<string, { month: string; commission: number }[]>();
    if (postReconData) {
      for (const row of postReconData.rows) {
        if (row.acMonthly && row.acMonthly.length > 0) {
          map.set(row.reconAccount.accountId, row.acMonthly);
        }
      }
    }
    return map;
  }, [postReconData]);

  const getCoverage = useCallback((iss: ComparisonIssue): Coverage => {
    const acm = acMonthlyByAccountId.get(iss.accountId) || [];
    const acMonths = new Set(acm.map(m => monthToSortKey(m.month)));
    return computeCoverage(iss, lastMonth, acMonths);
  }, [acMonthlyByAccountId, lastMonth]);

  const reportRows = useMemo(() => {
    if (!compareData) return [];
    const rows: ReportRow[] = [];
    for (const iss of compareData.issues) {
      // Recalculate postReconInvoiceRevenue filtered by lastMonth
      let filteredPostReconInv = iss.postReconInvoiceRevenue || 0;
      if (iss.invoiceMonthly && lastMonth < getCurrentMonth()) {
        const postReconMonths = iss.invoiceMonthly.filter(m => {
          const mk = monthToSortKey(m.month);
          return mk > '2024-11' && mk <= lastMonth;
        });
        filteredPostReconInv = postReconMonths.reduce((s, m) => s + m.revenue, 0);
      }

      // AC post-recon: sum monthly AC data up to lastMonth
      const acMonthly = acMonthlyByAccountId.get(iss.accountId);
      let acPost = 0;
      if (acMonthly) {
        for (const m of acMonthly) {
          if (monthToSortKey(m.month) <= lastMonth) acPost += m.commission;
        }
      } else {
        acPost = acPostReconByAccountId.get(iss.accountId) || 0;
      }

      const filtered = { ...iss, postReconInvoiceRevenue: filteredPostReconInv };
      const row = computeReportRow(filtered, acPost, mode);
      if (row) rows.push(row);
    }
    return rows;
  }, [compareData, acPostReconByAccountId, acMonthlyByAccountId, lastMonth, mode]);

  const filteredRows = useMemo(() => {
    let items = reportRows;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      items = items.filter(r =>
        r.issue.partner?.toLowerCase().includes(q) ||
        r.issue.brand?.toLowerCase().includes(q) ||
        r.issue.brands?.some(b => b.toLowerCase().includes(q)) ||
        r.issue.accountId?.toLowerCase().includes(q) ||
        r.issue.username?.toLowerCase().includes(q) ||
        r.issue.geo?.toLowerCase().includes(q) ||
        r.issue.adwstatsAccountName?.toLowerCase().includes(q)
      );
    }
    const getVal = (r: ReportRow): number | string => {
      switch (sortField) {
        case 'partner': return (r.issue.partner || '').toLowerCase();
        case 'accountId': return r.issue.accountId || '';
        case 'reconInvoiced': return r.reconInvoiced;
        case 'postReconInvoiced': return r.postReconInvoiced;
        case 'totalInvoiced': return r.totalInvoiced;
        case 'income': return r.incomeAmount;
        case 'acPostRecon': return r.acPostRecon;
        case 'totalEarned': return r.totalEarned;
        case 'underpaid': return r.underpaid;
        case 'pct': return r.underpaidPct;
        default: return 0;
      }
    };
    return [...items].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [reportRows, searchTerm, sortField, sortDir]);

  const totals = useMemo(() => {
    let reconInvoiced = 0, postReconInvoiced = 0, totalInvoiced = 0, income = 0, acPost = 0, totalEarned = 0, underpaid = 0;
    let accessAmount = 0, accessCount = 0, noAccessAmount = 0, noAccessCount = 0;
    let gapAmount = 0, gapCount = 0;
    for (const r of reportRows) {
      reconInvoiced += r.reconInvoiced;
      postReconInvoiced += r.postReconInvoiced;
      totalInvoiced += r.totalInvoiced;
      income += r.incomeAmount;
      acPost += r.acPostRecon;
      totalEarned += r.totalEarned;
      underpaid += r.underpaid;
      if (resolveAccess(r.issue).has) { accessAmount += r.underpaid; accessCount++; }
      else { noAccessAmount += r.underpaid; noAccessCount++; }
      const cov = getCoverage(r.issue);
      if (cov.uncoveredAmount > 0) { gapAmount += cov.uncoveredAmount; gapCount++; }
    }
    return { reconInvoiced, postReconInvoiced, totalInvoiced, income, acPost, totalEarned, underpaid, count: reportRows.length, accessAmount, accessCount, noAccessAmount, noAccessCount, gapAmount, gapCount };
  }, [reportRows, resolveAccess, getCoverage]);

  const toggleRow = (key: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortHeader: React.FC<{ field: SortField; label: string; align?: string; className?: string }> = ({ field, label, align, className }) => (
    <th
      className={`px-3 py-2.5 font-medium cursor-pointer hover:text-zinc-300 select-none whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'} ${className || 'text-zinc-500'}`}
      onClick={() => handleSort(field)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortField === field && (
          sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        )}
      </span>
    </th>
  );

  const sourceColor = (src: string) => {
    if (src.includes('Synced')) return 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20';
    if (src.includes('Manual')) return 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20';
    return 'text-amber-400 bg-amber-500/10 border-amber-500/20';
  };

  const sourceShort = (src: string) => {
    if (src.includes('Synced')) return 'Synced -5%';
    if (src.includes('Manual')) return 'Manual -5%';
    return 'Recon';
  };

  const hasAcData = postReconData != null;

  return (
    <div className="min-h-screen bg-[hsl(225,15%,6%)] p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight flex items-center gap-3">
              <FileText className={`h-7 w-7 ${isOver ? 'text-amber-400' : 'text-indigo-400'}`} />
              {isOver ? 'Overpaid Report' : 'Final Underpaid Report'}
            </h1>
            <p className="text-zinc-400 mt-1 text-sm">
              {isOver
                ? 'Accounts where Adwin was invoiced MORE than earned — potential offsets Raketech may raise'
                : 'Full period: Recon data (Mar 2023 – Nov 2024) + AC stats (Dec 2024+)'}
            </p>
            <div className="mt-3 inline-flex rounded-lg border border-[hsl(225,15%,22%)] bg-[hsl(225,15%,12%)] p-0.5">
              <button
                onClick={() => switchMode('underpaid')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!isOver ? 'bg-red-500/20 text-red-300' : 'text-zinc-400 hover:text-zinc-200'}`}
              >
                Underpaid (they owe us)
              </button>
              <button
                onClick={() => switchMode('overpaid')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${isOver ? 'bg-amber-500/20 text-amber-300' : 'text-zinc-400 hover:text-zinc-200'}`}
              >
                Overpaid (we over-invoiced)
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-1.5 px-2 py-1.5 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,22%)] rounded-lg">
              <Calendar className="h-3.5 w-3.5 text-zinc-400" />
              <span className="text-zinc-500">Up to</span>
              <input
                type="month"
                value={lastMonth}
                min="2024-12"
                max={getCurrentMonth()}
                onChange={e => { if (e.target.value) setLastMonth(e.target.value); }}
                className="bg-transparent text-white text-xs font-medium outline-none cursor-pointer w-[7.5rem] [color-scheme:dark]"
              />
            </div>
            {isACConnected ? (
              <span className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <Shield className="h-3.5 w-3.5" /> AC Connected
              </span>
            ) : (
              <button
                onClick={() => setShowTokenInput(!showTokenInput)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-amber-400 hover:text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg transition-colors"
              >
                <Key className="h-3.5 w-3.5" /> Connect AC
              </button>
            )}
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[hsl(225,15%,15%)] hover:bg-[hsl(225,15%,20%)] rounded-lg border border-[hsl(225,15%,22%)] transition-colors disabled:opacity-50 shrink-0"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            {reportRows.length > 0 && !isOver && (
              <button
                onClick={() => {
                  const monthName = new Date(lastMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
                  setShareTitle(`Raketech Report — up to ${monthName}`);
                  setShareDesc('');
                  setSharePassword('');
                  setSavedUrl(null);
                  setShowShareModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shrink-0"
              >
                <Share2 className="h-4 w-4" /> Save & Share
              </button>
            )}
            {snapshots.length > 0 && (
              <button
                onClick={() => setShowSnapshots(!showSnapshots)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-zinc-400 hover:text-zinc-300 bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,22%)] rounded-lg transition-colors"
              >
                <Link className="h-3.5 w-3.5" /> {snapshots.length} saved
              </button>
            )}
            {isACConnected && (
              <button onClick={handleDisconnect} className="text-xs text-zinc-500 hover:text-zinc-400 transition-colors">
                Disconnect
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AC Token Input */}
      {showTokenInput && !isACConnected && (
        <div className="mb-6 bg-[hsl(225,15%,10%)] rounded-xl border border-amber-500/20 p-4">
          <p className="text-xs text-zinc-400 mb-3">Paste your AC Bearer token to include post-recon earnings</p>
          <div className="flex gap-2">
            <input type="password" value={tokenInput} onChange={e => setTokenInput(e.target.value)}
              placeholder="Paste AC Bearer token..."
              className="flex-1 px-3 py-2 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50" />
            <button onClick={handleSaveToken} className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors">Save</button>
            <button onClick={() => { setShowTokenInput(false); setTokenInput(''); }} className="px-3 py-2 text-sm text-zinc-400 hover:text-zinc-300">Cancel</button>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => !isSaving && setShowShareModal(false)}>
          <div className="bg-[hsl(225,15%,10%)] border border-[hsl(225,15%,20%)] rounded-2xl w-full max-w-md mx-4 p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            {!savedUrl ? (
              <>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Share2 className="h-5 w-5 text-indigo-400" /> Save & Share Report
                  </h3>
                  <button onClick={() => setShowShareModal(false)} className="text-zinc-500 hover:text-zinc-300"><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">Title</label>
                    <input value={shareTitle} onChange={e => setShareTitle(e.target.value)}
                      className="w-full px-3 py-2 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50" />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5">Description (optional)</label>
                    <textarea value={shareDesc} onChange={e => setShareDesc(e.target.value)} rows={2}
                      placeholder="Notes for the recipient..."
                      className="w-full px-3 py-2 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50 resize-none" />
                  </div>
                  <div>
                    <label className="block text-xs text-zinc-400 mb-1.5 flex items-center gap-1.5">
                      <Lock className="h-3 w-3" /> Password (optional)
                    </label>
                    <input type="text" value={sharePassword} onChange={e => setSharePassword(e.target.value)}
                      placeholder="Leave empty for no password"
                      className="w-full px-3 py-2 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50" />
                  </div>
                  <div className="bg-[hsl(225,15%,8%)] rounded-lg p-3 text-xs text-zinc-500">
                    <p>This will snapshot <strong className="text-zinc-300">{reportRows.length}</strong> accounts as of <strong className="text-zinc-300">{lastMonth}</strong>.</p>
                    <p className="mt-1">Anyone with the link{sharePassword ? ' and password' : ''} can view the report.</p>
                  </div>
                  <button
                    onClick={handleSaveSnapshot}
                    disabled={isSaving || !shareTitle.trim()}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />}
                    {isSaving ? 'Saving...' : 'Create Shareable Link'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Check className="h-5 w-5 text-emerald-400" /> Report Saved
                  </h3>
                  <button onClick={() => setShowShareModal(false)} className="text-zinc-500 hover:text-zinc-300"><X className="h-5 w-5" /></button>
                </div>
                <div className="space-y-4">
                  <p className="text-sm text-zinc-400">Your report snapshot has been created. Share this link:</p>
                  <div className="flex gap-2">
                    <input readOnly value={savedUrl}
                      className="flex-1 px-3 py-2 text-sm bg-[hsl(225,15%,14%)] border border-[hsl(225,15%,22%)] rounded-lg text-indigo-300 font-mono text-xs focus:outline-none" />
                    <button onClick={() => copyToClipboard(savedUrl)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shrink-0">
                      {copiedUrl ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {copiedUrl ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                  <a href={savedUrl} target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full px-4 py-2 text-sm text-indigo-400 hover:text-indigo-300 bg-indigo-500/10 border border-indigo-500/20 rounded-lg transition-colors">
                    <ExternalLink className="h-4 w-4" /> Open in new tab
                  </a>
                  {sharePassword && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-400 flex items-start gap-2">
                      <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>Password protected. Share the password separately: <strong className="font-mono">{sharePassword}</strong></span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Saved Snapshots Panel */}
      {showSnapshots && snapshots.length > 0 && (
        <div className="mb-6 bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-white flex items-center gap-2">
              <Link className="h-4 w-4 text-indigo-400" /> Saved Report Snapshots
            </h3>
            <button onClick={() => setShowSnapshots(false)} className="text-zinc-500 hover:text-zinc-300"><X className="h-4 w-4" /></button>
          </div>
          <div className="space-y-2">
            {snapshots.map(s => (
              <div key={s.id} className="flex items-center justify-between gap-3 px-3 py-2 bg-[hsl(225,15%,8%)] rounded-lg text-xs">
                <div className="min-w-0">
                  <div className="text-white font-medium truncate">{s.title}</div>
                  <div className="text-zinc-500 flex items-center gap-2 mt-0.5">
                    <span>Up to {s.lastMonth}</span>
                    <span>{new Date(s.createdAt).toLocaleDateString()}</span>
                    <span>{s.viewCount} views</span>
                    {s.hasPassword && <Lock className="h-3 w-3 text-amber-400" />}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => { copyToClipboard(`${window.location.origin}${s.url}`); toast.success('Link copied'); }}
                    className="p-1.5 text-zinc-400 hover:text-white transition-colors" title="Copy link">
                    <Copy className="h-3.5 w-3.5" />
                  </button>
                  <a href={s.url} target="_blank" rel="noopener noreferrer"
                    className="p-1.5 text-zinc-400 hover:text-indigo-400 transition-colors" title="Open">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button onClick={() => handleDeleteSnapshot(s.id)}
                    className="p-1.5 text-zinc-400 hover:text-red-400 transition-colors" title="Delete">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-indigo-400 mr-3" />
          <span className="text-zinc-400">Loading report data...</span>
        </div>
      )}

      {error && !isLoading && (
        <div className="max-w-lg mx-auto bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-8 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">No Data Available</h2>
          <p className="text-sm text-zinc-400">{error}</p>
        </div>
      )}

      {compareData && !isLoading && (
        <div className="space-y-6 animate-fade-in">
          {/* Summary */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard label={isOver ? 'Overpaid Accounts' : 'Underpaid Accounts'} value={String(totals.count)} icon={<AlertTriangle className="h-4 w-4" />} color={isOver ? 'amber' : 'red'} />
            <SummaryCard label="Total Invoiced" value={fmtCurrency(totals.totalInvoiced)} icon={<FileText className="h-4 w-4" />} color="zinc" />
            <SummaryCard label="Total Earned" value={fmtCurrency(totals.totalEarned)} icon={<Scale className="h-4 w-4" />} color="emerald" />
            <SummaryCard label={isOver ? 'Total Overpaid' : 'Total Underpaid'} value={fmtCurrency(totals.underpaid)} icon={<AlertTriangle className="h-4 w-4" />} color={isOver ? 'amber' : 'red'} />
          </div>

          {/* Breakdown row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <SummaryCard label="Recon Invoiced" value={fmtCurrency(totals.reconInvoiced)} icon={<FileText className="h-4 w-4" />} color="zinc" />
            <SummaryCard label="Post-Recon Invoiced" value={fmtCurrency(totals.postReconInvoiced)} icon={<FileText className="h-4 w-4" />} color="zinc" />
            <SummaryCard label="Recon Income" value={fmtCurrency(totals.income)} icon={<Scale className="h-4 w-4" />} color="amber" />
            {hasAcData ? (
              <SummaryCard label="AC Post-Recon Income" value={fmtCurrency(totals.acPost)} icon={<Shield className="h-4 w-4" />} color="indigo" />
            ) : (
              <SummaryCard label="AC Post-Recon" value="Connect AC" icon={<Shield className="h-4 w-4" />} color="zinc" />
            )}
          </div>

          {/* Access split */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-4">
              <div className="flex items-center gap-2 mb-1">
                <Key className="h-4 w-4 text-emerald-400" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">
                  {isOver ? 'Overpaid on accounts we CAN verify' : 'Underpaid on accounts we CAN verify'}
                </span>
              </div>
              <p className="text-lg sm:text-xl font-bold tabular-nums text-emerald-400">{fmtCurrency(totals.accessAmount)}</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">{totals.accessCount} accounts · synced, manually checked, or connected in adwstats</p>
            </div>
            <div className="rounded-xl border border-red-500/20 bg-red-500/[0.06] p-4">
              <div className="flex items-center gap-2 mb-1">
                <Lock className="h-4 w-4 text-red-400" />
                <span className="text-[10px] text-zinc-400 uppercase tracking-wider">
                  {isOver ? 'Overpaid on NO-access accounts' : 'Underpaid on NO-access accounts'}
                </span>
              </div>
              <p className="text-lg sm:text-xl font-bold tabular-nums text-red-400">{fmtCurrency(totals.noAccessAmount)}</p>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                {totals.noAccessCount} accounts · recon-only{isOver ? ' — cannot be accepted as offsets until opened at source' : ' — figures rest on Raketech reports alone'}
              </p>
            </div>
          </div>

          {/* Coverage gap banner */}
          {totals.gapAmount > 0 && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.07] p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
                <div className="text-xs text-zinc-300 space-y-1">
                  <p className="text-sm font-semibold text-amber-300">
                    Data coverage gap: {fmtCurrency(totals.gapAmount)} invoiced with no earned data ({totals.gapCount} accounts)
                  </p>
                  <p className="text-zinc-400">
                    These are invoiced months with <strong>no Recon, no Synced and no AC</strong> figure to compare against — recon ends Nov 2024 and AC only begins mid-2025 for many accounts, leaving a hole around <strong>Dec 2024 – May 2025</strong>. Such months cannot be judged over- or under-paid: in {isOver ? 'this overpaid view they inflate the "overpayment"' : 'the underpaid view they may hide real underpayments'}. Flagged per month in each account drilldown.
                  </p>
                  {!hasAcData && (
                    <p className="text-amber-400/80">Note: AC is not connected, so this gap is overstated — connect AC to fold in Dec 2024+ earnings.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Info banner */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] p-4 text-xs text-zinc-400 space-y-1">
            <p><strong className="text-zinc-300">Recon Income:</strong> MAX(Reconciliation, Synced×0.95, ManualCheck×0.95) — best available source for Mar 2023 – Nov 2024</p>
            {hasAcData && (
              <p><strong className="text-indigo-300">AC Post-Recon Income:</strong> Commission reported by Affiliation Cloud for Dec 2024+</p>
            )}
            {isOver ? (
              <p><strong className="text-amber-300">Overpaid:</strong> Total Invoiced − Total Earned. Positive = we invoiced more than the available source data shows we earned. These are the accounts Raketech would point to as offsets — review whether the "earned" side is genuinely complete (shared / no-access accounts may understate earnings and create a false overpayment).</p>
            ) : (
              <p><strong className="text-zinc-300">Underpaid:</strong> Total Invoiced − Total Earned. Negative = they charged less than we earned.</p>
            )}
            <p><strong className="text-zinc-300">Access:</strong> <span className="text-emerald-400">Synced</span> = direct operator-backend sync · <span className="text-cyan-400">Manual</span> = backend checked by hand · <span className="text-sky-400">Tracked</span> = account connected in adwstats · <span className="text-violet-400">Login</span> = matching login exists in /networks · <span className="text-red-400">No access</span> = no login on record, figure rests on Raketech reports and cannot be verified at source.</p>
          </div>

          {/* Period info */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-indigo-500/20 p-3 text-xs flex flex-wrap gap-6">
            <div>
              <span className="text-zinc-500">Recon period: </span>
              <span className="text-white font-medium">Mar 2023 – Nov 2024</span>
            </div>
            <div>
              <span className="text-zinc-500">Post-recon: </span>
              <span className={hasAcData ? 'text-emerald-400 font-medium' : 'text-zinc-600'}>
                {hasAcData && postReconData ? postReconData.period.postRecon : 'Connect AC to include'}
              </span>
            </div>
            {postReconData && (
              <div>
                <span className="text-zinc-500">AC matched: </span>
                <span className="text-emerald-400 font-medium">{postReconData.summary.matched}</span>
                <span className="text-zinc-600"> / {postReconData.summary.totalReconAccounts}</span>
              </div>
            )}
          </div>

          {/* Search */}
          <div className="flex items-center gap-3">
            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search partner, brand, account..."
              className="px-3 py-1.5 w-72 text-xs bg-[hsl(225,15%,12%)] border border-[hsl(225,15%,20%)] rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-indigo-500/50" />
            <span className="text-xs text-zinc-500">{filteredRows.length} accounts</span>
          </div>

          {/* Table */}
          <div className="bg-[hsl(225,15%,10%)] rounded-xl border border-[hsl(225,15%,18%)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-[hsl(225,15%,8%)] sticky top-0">
                  <tr>
                    <th className="w-8 px-2 py-2.5"></th>
                    <SortHeader field="partner" label="Partner / Brand" />
                    <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">GEO</th>
                    <SortHeader field="accountId" label="Account" />
                    <SortHeader field="reconInvoiced" label="Recon Inv." align="right" />
                    <SortHeader field="income" label="Recon Income" align="right" />
                    <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Source</th>
                    <th className="px-3 py-2.5 text-left text-zinc-500 font-medium">Access</th>
                    <SortHeader field="postReconInvoiced" label="Post-Recon Inv." align="right" className="text-zinc-400" />
                    {hasAcData && <SortHeader field="acPostRecon" label="AC Income" align="right" className="text-indigo-400/70" />}
                    <SortHeader field="totalInvoiced" label="Total Inv." align="right" />
                    <SortHeader field="totalEarned" label="Total Earned" align="right" className="text-emerald-400/70" />
                    <SortHeader field="underpaid" label={isOver ? 'Overpaid' : 'Underpaid'} align="right" />
                    <SortHeader field="pct" label="%" align="right" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(225,15%,13%)]">
                  {filteredRows.map((row, idx) => {
                    const key = `${row.issue.accountId}-${row.issue.offer}-${idx}`;
                    const isExpanded = expandedRows.has(key);
                    const coverage = getCoverage(row.issue);
                    const entries = row.issue.invoiceEntries;
                    const monthly = sortMonthly(row.issue.invoiceMonthly || []);
                    const hasEntries = entries && entries.length > 0;
                    const hasAcMonthly = (acMonthlyByAccountId.get(row.issue.accountId)?.length || 0) > 0;
                    const hasReconOrSynced = (row.issue.reconMonthly?.length || 0) > 0 || (row.issue.syncedMonthly?.length || 0) > 0;
                    const hasDrilldown = hasEntries || monthly.length > 0 || hasReconOrSynced || hasAcMonthly;
                    const colSpan = hasAcData ? 13 : 12;

                    return (
                      <React.Fragment key={key}>
                        <tr className="hover:bg-[hsl(225,15%,12%)] cursor-pointer" onClick={() => toggleRow(key)}>
                          <td className="px-2 py-2 text-zinc-500">
                            {hasDrilldown ? (isExpanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />) : null}
                          </td>
                          <td className="px-3 py-2">
                            <div className="text-white font-medium">{row.issue.partner}</div>
                            <div className="text-zinc-500 text-[10px]" title={row.issue.brands?.join(', ')}>
                              {row.issue.brands && row.issue.brands.length > 1
                                ? row.issue.brands.join(', ')
                                : row.issue.brand}
                              {row.issue.offer ? ` — ${row.issue.offer}` : ''}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-purple-400 font-medium">{row.issue.geo}</td>
                          <td className="px-3 py-2">
                            <div className="text-zinc-300 tabular-nums">{row.issue.accountId}</div>
                            <div className="text-zinc-500 text-[10px] truncate max-w-[140px]">{row.issue.username}</div>
                            {row.issue.adwstatsAccountName && (
                              <div className="text-emerald-500/70 text-[10px] truncate max-w-[140px]" title={row.issue.adwstatsAccountName}>
                                ↔ {row.issue.adwstatsAccountName}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{fmtCurrency(row.reconInvoiced)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            <div className="text-amber-400">{fmtCurrency(row.incomeAmount)}</div>
                            {row.incomeSource !== 'Reconciliation' && (
                              <div className="text-[9px] text-zinc-500">was {fmtCurrency(row.incomeOriginal)}</div>
                            )}
                            {row.incomeOriginalCurrency && row.incomeOriginalAmount != null && (
                              <div className="text-[9px] text-zinc-500">
                                {fmtOriginal(row.incomeOriginalAmount, row.incomeOriginalCurrency)}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium ${sourceColor(row.incomeSource)}`}>
                              {sourceShort(row.incomeSource)}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            {(() => { const a = resolveAccess(row.issue); return (
                              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-medium ${accessColor(a.tier)}`} title={a.detail}>
                                {a.has ? <Key className="h-2.5 w-2.5" /> : <Lock className="h-2.5 w-2.5" />}
                                {a.label}
                              </span>
                            ); })()}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {row.postReconInvoiced > 0 ? (
                              <span className="text-zinc-300">{fmtCurrency(row.postReconInvoiced)}</span>
                            ) : <span className="text-zinc-700">—</span>}
                          </td>
                          {hasAcData && (
                            <td className="px-3 py-2 text-right tabular-nums">
                              {row.acPostRecon > 0 ? (
                                <span className="text-indigo-400">{fmtCurrency(row.acPostRecon)}</span>
                              ) : <span className="text-zinc-700">—</span>}
                            </td>
                          )}
                          <td className="px-3 py-2 text-right text-zinc-300 tabular-nums">{fmtCurrency(row.totalInvoiced)}</td>
                          <td className="px-3 py-2 text-right text-emerald-400 tabular-nums font-medium">{fmtCurrency(row.totalEarned)}</td>
                          <td className={`px-3 py-2 text-right tabular-nums font-medium ${isOver ? 'text-amber-400' : 'text-red-400'}`}>
                            {fmtCurrency(row.underpaid)}
                            {coverage.uncoveredAmount > 0 && (
                              <div className="text-[9px] text-amber-500/90 font-normal" title={`Invoiced with no Recon/Synced/AC data: ${[...coverage.uncoveredMonths].sort().map(fmtMonth).join(', ')}`}>
                                gap {fmtCurrency(coverage.uncoveredAmount)}
                              </div>
                            )}
                          </td>
                          <td className={`px-3 py-2 text-right tabular-nums ${isOver ? 'text-amber-400' : 'text-red-400'}`}>{row.underpaidPct.toFixed(1)}%</td>
                        </tr>

                        {/* Drilldown */}
                        {isExpanded && hasDrilldown && (() => {
                          const reconM = row.issue.reconMonthly || [];
                          const syncedM = row.issue.syncedMonthly || [];
                          const acM = acMonthlyByAccountId.get(row.issue.accountId) || [];
                          const hasComparison = reconM.length > 0 || syncedM.length > 0 || acM.length > 0;

                          const invoiceByMonth = new Map<string, InvoiceMonth>();
                          if (hasEntries && entries) {
                            for (const entry of entries) {
                              for (const m of (entry.monthly || [])) {
                                const mk = monthToSortKey(m.month);
                                const existing = invoiceByMonth.get(mk);
                                if (existing) { existing.ftds += m.ftds; existing.revenue += m.revenue; }
                                else invoiceByMonth.set(mk, { month: m.month, ftds: m.ftds, revenue: m.revenue });
                              }
                            }
                          } else {
                            for (const m of monthly) invoiceByMonth.set(monthToSortKey(m.month), m);
                          }

                          const allMonthsSet = new Set<string>();
                          invoiceByMonth.forEach((_, k) => allMonthsSet.add(k));
                          reconM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                          syncedM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                          acM.forEach(m => allMonthsSet.add(monthToSortKey(m.month)));
                          const allMonths = [...allMonthsSet].filter(m => m <= lastMonth).sort();

                          const reconByMonth = new Map(reconM.map(m => [monthToSortKey(m.month), m]));
                          const syncedByMonth = new Map(syncedM.map(m => [monthToSortKey(m.month), m]));
                          const acByMonth = new Map(acM.map(m => [monthToSortKey(m.month), m]));

                          return (
                            <tr>
                              <td colSpan={colSpan} className="bg-[hsl(225,15%,8%)] px-6 py-3">
                                <div className="space-y-4">
                                  {hasEntries && entries && (
                                    <>
                                      <div className="text-[10px] text-zinc-400 font-medium uppercase tracking-wider">
                                        Invoice Breakdown — {row.issue.partner} / {row.issue.brands && row.issue.brands.length > 1
                                          ? row.issue.brands.join(', ')
                                          : row.issue.brand}
                                      </div>
                                      {entries.map((entry, ei) => {
                                        const sorted = sortMonthly(entry.monthly || []);
                                        return (
                                          <div key={ei} className="border border-[hsl(225,15%,15%)] rounded-lg p-3">
                                            <div className="flex items-center justify-between mb-2">
                                              <span className="text-[11px] text-amber-400 font-medium truncate max-w-[500px]">{entry.label}</span>
                                              <span className="text-[11px] text-zinc-300 tabular-nums font-medium">{fmtCurrency(entry.revenue)}</span>
                                            </div>
                                            {sorted.length > 0 && (
                                              <table className="w-full max-w-xl text-xs">
                                                <thead><tr className="text-zinc-500">
                                                  <th className="text-left py-1 px-2 font-medium">Month</th>
                                                  <th className="text-right py-1 px-2 font-medium">FTDs</th>
                                                  <th className="text-right py-1 px-2 font-medium">Revenue (EUR)</th>
                                                </tr></thead>
                                                <tbody className="divide-y divide-[hsl(225,15%,11%)]">
                                                  {sorted.map(m => (
                                                    <tr key={m.month} className="text-zinc-300">
                                                      <td className="py-1 px-2">{fmtMonth(m.month)}</td>
                                                      <td className="py-1 px-2 text-right tabular-nums">{m.ftds.toLocaleString()}</td>
                                                      <td className="py-1 px-2 text-right tabular-nums">{fmtCurrency(m.revenue)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </>
                                  )}

                                  {hasComparison && allMonths.length > 0 && (
                                    <div className="border border-[hsl(225,15%,15%)] rounded-lg p-3">
                                      <div className="text-[10px] text-zinc-400 font-medium uppercase tracking-wider mb-2">
                                        Monthly Comparison
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                          <thead><tr className="text-zinc-500">
                                            <th className="text-left py-1 px-2 font-medium">Month</th>
                                            <th className="text-right py-1 px-2 font-medium text-zinc-400">Invoice</th>
                                            <th className="text-right py-1 px-2 font-medium text-amber-400/70">Recon</th>
                                            {syncedM.length > 0 && <th className="text-right py-1 px-2 font-medium text-emerald-400/70">Synced(-5%)</th>}
                                            {acM.length > 0 && <th className="text-right py-1 px-2 font-medium text-indigo-400/70">AC</th>}
                                            <th className="text-right py-1 px-2 font-medium text-red-400/70">Diff</th>
                                          </tr></thead>
                                          <tbody className="divide-y divide-[hsl(225,15%,11%)]">
                                            {allMonths.map(m => {
                                              const inv = invoiceByMonth.get(m);
                                              const rec = reconByMonth.get(m);
                                              const syn = syncedByMonth.get(m);
                                              const ac = acByMonth.get(m);
                                              const invAmt = inv?.revenue || 0;
                                              const acAmt = ac?.commission || 0;
                                              const bestIncome = Math.max(rec?.earnings || 0, (syn?.commission || 0) * 0.95, acAmt);
                                              const diff = invAmt > 0 && bestIncome > 0 ? invAmt - bestIncome : 0;
                                              const diffColor = Math.abs(diff) < 1 ? 'text-zinc-600' : diff < 0 ? 'text-emerald-400' : 'text-red-400';
                                              // Gap month: invoice exists but NO recon, synced or AC earned data
                                              const isGap = invAmt > 0 && !(rec && (rec.earnings || 0) > 0) && !(syn && (syn.commission || 0) > 0) && acAmt <= 0;
                                              return (
                                                <tr key={m} className={isGap ? 'text-zinc-300 bg-amber-500/[0.07]' : 'text-zinc-300'}>
                                                  <td className="py-1 px-2">{fmtMonth(m)}</td>
                                                  <td className="py-1 px-2 text-right tabular-nums">{invAmt > 0 ? fmtCurrency(invAmt) : <span className="text-zinc-600">—</span>}</td>
                                                  <td className="py-1 px-2 text-right tabular-nums text-amber-400">{rec ? fmtCurrency(rec.earnings) : <span className="text-zinc-600">—</span>}</td>
                                                  {syncedM.length > 0 && <td className="py-1 px-2 text-right tabular-nums text-emerald-400">{syn ? fmtCurrency(syn.commission * 0.95) : <span className="text-zinc-600">—</span>}</td>}
                                                  {acM.length > 0 && <td className="py-1 px-2 text-right tabular-nums text-indigo-400">{acAmt > 0 ? fmtCurrency(acAmt) : <span className="text-zinc-600">—</span>}</td>}
                                                  <td className={`py-1 px-2 text-right tabular-nums ${isGap ? 'text-amber-400' : diffColor}`}>
                                                    {isGap
                                                      ? <span className="inline-flex items-center gap-1" title="Invoiced, but no Recon / Synced / AC earned data for this month"><AlertTriangle className="h-2.5 w-2.5" /> no data</span>
                                                      : (Math.abs(diff) > 0.01 ? fmtCurrency(diff) : <span className="text-zinc-600">—</span>)}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}

                                  {row.acPostRecon > 0 && (
                                    <div className="border border-indigo-500/20 rounded-lg p-3">
                                      <div className="text-[10px] text-indigo-400 font-medium uppercase tracking-wider mb-1">
                                        AC Post-Recon Earnings (Dec 2024+)
                                      </div>
                                      <span className="text-sm text-indigo-400 font-bold tabular-nums">{fmtCurrency(row.acPostRecon)}</span>
                                      <span className="text-[10px] text-zinc-500 ml-2">from Affiliation Cloud reports</span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredRows.length === 0 && (
              <div className="p-8 text-center text-zinc-500 text-sm">{isOver ? 'No overpaid accounts found' : 'No underpaid accounts found'}</div>
            )}
          </div>

          {/* Totals footer */}
          <div className={`bg-[hsl(225,15%,10%)] rounded-xl border p-4 ${isOver ? 'border-amber-500/20' : 'border-red-500/20'}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="text-sm text-zinc-400">
                <strong className="text-white">{totals.count}</strong> {isOver ? 'overpaid' : 'underpaid'} accounts
              </div>
              <div className="flex items-center gap-6 text-sm flex-wrap">
                <div>
                  <span className="text-zinc-500">Invoiced: </span>
                  <span className="text-white tabular-nums font-medium">{fmtCurrency(totals.totalInvoiced)}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Earned: </span>
                  <span className="text-emerald-400 tabular-nums font-medium">{fmtCurrency(totals.totalEarned)}</span>
                </div>
                <div>
                  <span className="text-zinc-500">{isOver ? 'Overpaid: ' : 'Underpaid: '}</span>
                  <span className={`tabular-nums font-bold ${isOver ? 'text-amber-400' : 'text-red-400'}`}>{fmtCurrency(totals.underpaid)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
const SummaryCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string }> = ({ label, value, icon, color }) => {
  const colorMap: Record<string, string> = {
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
    indigo: 'text-indigo-400 bg-indigo-500/10 border-indigo-500/20',
    zinc: 'text-zinc-300 bg-[hsl(225,15%,12%)] border-[hsl(225,15%,18%)]',
  };
  const c = colorMap[color] || colorMap.zinc;
  const parts = c.split(' ');
  return (
    <div className={`rounded-xl border p-4 ${parts.slice(1).join(' ')}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={parts[0]}>{icon}</span>
        <span className="text-[10px] text-zinc-400 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg sm:text-xl font-bold tabular-nums ${parts[0]}`}>{value}</p>
    </div>
  );
};

export default RaketechFinalReport;
